import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Download, FileArchive, Loader2, ShieldCheck, Clock, AlertTriangle } from "lucide-react";
import {
  getStudentExportSections, createStudentExport, getStudentExportJobs, getStudentExportJob,
} from "../../lib/api";
import { downloadFromApi } from "../../lib/download";

const STATUS_STYLE = {
  ready: "bg-emerald-100 text-emerald-800 border-emerald-300",
  running: "bg-blue-100 text-blue-800 border-blue-300",
  queued: "bg-slate-100 text-slate-700 border-slate-300",
  failed: "bg-red-100 text-red-800 border-red-300",
  expired: "bg-amber-100 text-amber-800 border-amber-300",
};

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export function StudentRecordExportDialog({ open, onOpenChange, student }) {
  const studentId = student?.id;
  const [sections, setSections] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [selected, setSelected] = useState({});
  const [dateMode, setDateMode] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [redact, setRedact] = useState(true);
  const [purpose, setPurpose] = useState("");
  const [purposeOther, setPurposeOther] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);

  const loadHistory = useCallback(() => {
    if (!studentId) return;
    getStudentExportJobs(studentId).then((r) => setHistory(r.data || [])).catch(() => {});
  }, [studentId]);

  useEffect(() => {
    if (!open || !studentId) return;
    setJob(null);
    getStudentExportSections(studentId)
      .then((r) => {
        const secs = r.data?.sections || [];
        setSections(secs);
        setPurposes(r.data?.purposes || []);
        setWarnings(r.data?.warnings || []);
        const init = {};
        secs.forEach((s) => { init[s.key] = true; });
        setSelected(init);
      })
      .catch(() => toast.error("Could not load export options"));
    loadHistory();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, studentId, loadHistory]);

  const poll = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await getStudentExportJob(jobId);
        setJob(r.data);
        if (["ready", "failed", "expired"].includes(r.data.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          loadHistory();
          if (r.data.status === "ready") toast.success("Export ready to download");
          if (r.data.status === "failed") toast.error("Export failed: " + (r.data.error || "unknown error"));
        }
      } catch { /* keep polling */ }
    }, 2000);
  }, [loadHistory]);

  const handleGenerate = async () => {
    const chosen = sections.filter((s) => selected[s.key]).map((s) => s.key);
    if (!chosen.length) { toast.error("Select at least one section"); return; }
    if (!purpose) { toast.error("Choose a purpose"); return; }
    if (purpose === "other" && !purposeOther.trim()) { toast.error("Describe the purpose"); return; }
    if (dateMode === "custom" && !startDate && !endDate) { toast.error("Enter a start or end date"); return; }
    setLoading(true);
    try {
      const r = await createStudentExport({
        student_id: studentId,
        sections: chosen,
        date_mode: dateMode,
        start_date: startDate || null,
        end_date: endDate || null,
        redact,
        purpose,
        purpose_other: purposeOther,
        zip_password: password,
      });
      setJob(r.data);
      poll(r.data.id);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start export");
    } finally {
      setLoading(false);
    }
  };

  const doDownload = async (j) => {
    try {
      await downloadFromApi(`/api/student-export/jobs/${j.id}/download`, j.filename || "student_record.zip");
    } catch {
      toast.error("Download failed — the link may have expired");
      loadHistory();
    }
  };

  const building = job && ["queued", "running"].includes(job.status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="student-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#00305E]">
            <FileArchive className="w-5 h-5" /> Export Student Record
          </DialogTitle>
          <DialogDescription>
            Package everything on {student?.preferred_name || student?.first_name} {student?.last_name} into a single ZIP download.
          </DialogDescription>
        </DialogHeader>

        {building ? (
          <div className="py-6 space-y-3" data-testid="export-progress">
            <div className="flex items-center gap-2 text-sm text-[#00305E] font-medium">
              <Loader2 className="w-4 h-4 animate-spin" /> {job.step || "Preparing"}…
            </div>
            <Progress value={job.progress || 0} />
            <p className="text-xs text-muted-foreground">
              Large exports keep building here — you'll also get a notification when it's ready. You can close this and come back; it will appear under Previous exports.
            </p>
          </div>
        ) : job && job.status === "ready" ? (
          <div className="py-6 space-y-3 text-center" data-testid="export-ready">
            <ShieldCheck className="w-10 h-10 text-emerald-600 mx-auto" />
            <p className="font-medium text-[#00305E]">Your export is ready</p>
            <p className="text-xs text-muted-foreground">
              {job.file_count} files · {job.size_bytes >= 1048576 ? `${(job.size_bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(job.size_bytes / 1024))} KB`} · link expires {fmt(job.expires_at)}
            </p>
            <div className="flex justify-center gap-2">
              <Button onClick={() => doDownload(job)} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="export-download-btn">
                <Download className="w-4 h-4 mr-2" /> Download ZIP
              </Button>
              <Button variant="outline" onClick={() => setJob(null)} data-testid="export-new-btn">New export</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Date range */}
            <div>
              <Label className="text-sm font-semibold text-[#00305E]">Date range</Label>
              <RadioGroup value={dateMode} onValueChange={setDateMode} className="mt-2 space-y-1">
                <div className="flex items-center gap-2"><RadioGroupItem value="all" id="dr-all" data-testid="date-all" /><Label htmlFor="dr-all" className="font-normal">All time</Label></div>
                <div className="flex items-center gap-2"><RadioGroupItem value="current_year" id="dr-cy" data-testid="date-year" /><Label htmlFor="dr-cy" className="font-normal">Current school year</Label></div>
                <div className="flex items-center gap-2"><RadioGroupItem value="custom" id="dr-cu" data-testid="date-custom" /><Label htmlFor="dr-cu" className="font-normal">Custom dates</Label></div>
              </RadioGroup>
              {dateMode === "custom" && (
                <div className="flex gap-3 mt-2">
                  <div className="flex-1"><Label className="text-xs">Start</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="date-start" /></div>
                  <div className="flex-1"><Label className="text-xs">End</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="date-end" /></div>
                </div>
              )}
            </div>

            {/* Sections */}
            <div>
              {warnings.length > 0 && (
                <div className="mb-3 flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="export-warnings">
                  <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-800 space-y-1">
                    {warnings.map((w) => <p key={w.key} data-testid={`export-warning-${w.key}`}>{w.message}</p>)}
                  </div>
                </div>
              )}
              <Label className="text-sm font-semibold text-[#00305E]">Sections to include</Label>
              <div className="mt-2 space-y-2 border rounded-lg p-3">
                {sections.map((s) => (
                  <div key={s.key} className="flex items-start gap-2">
                    <Checkbox
                      id={`sec-${s.key}`}
                      checked={!!selected[s.key]}
                      onCheckedChange={(v) => setSelected((p) => ({ ...p, [s.key]: !!v }))}
                      data-testid={`section-${s.key}`}
                    />
                    <Label htmlFor={`sec-${s.key}`} className="font-normal leading-snug flex-1 cursor-pointer">
                      <span className="font-medium text-slate-800">{s.label.split(" (")[0]}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {s.count > 0 ? `${s.count} record${s.count === 1 ? "" : "s"}` : "No records"}
                      </span>
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Redaction */}
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <Checkbox id="redact" checked={redact} onCheckedChange={(v) => setRedact(!!v)} data-testid="redact-checkbox" className="mt-0.5" />
              <Label htmlFor="redact" className="font-normal leading-snug cursor-pointer">
                <span className="font-medium text-amber-900">Redact other students' names</span>
                <span className="block text-xs text-amber-800">Any other student named in this record appears as "Student A", "Student B", etc. (including initials).</span>
              </Label>
            </div>

            {/* Purpose */}
            <div>
              <Label className="text-sm font-semibold text-[#00305E]">Purpose <span className="text-red-500">*</span></Label>
              <Select value={purpose} onValueChange={setPurpose}>
                <SelectTrigger className="mt-2" data-testid="purpose-select"><SelectValue placeholder="Select a purpose" /></SelectTrigger>
                <SelectContent>
                  {purposes.map((p) => (<SelectItem key={p.value} value={p.value} data-testid={`purpose-${p.value}`}>{p.label}</SelectItem>))}
                </SelectContent>
              </Select>
              {purpose === "other" && (
                <Input className="mt-2" placeholder="Describe the purpose" value={purposeOther} onChange={(e) => setPurposeOther(e.target.value)} data-testid="purpose-other" />
              )}
            </div>

            {/* Password */}
            <div>
              <Label className="text-sm font-semibold text-[#00305E]">Password-protect the ZIP <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <Input type="text" className="mt-2" placeholder="Leave blank for no password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="zip-password" />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleGenerate} disabled={loading} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="export-generate-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileArchive className="w-4 h-4 mr-2" />}
                Generate Export
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="border-t pt-3 mt-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Previous exports</p>
            <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="export-history">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between gap-2 text-sm border rounded-md px-3 py-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={STATUS_STYLE[h.status] || ""}>{h.status}</Badge>
                      <span className="font-medium text-slate-800 truncate">{h.purpose_label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                      <Clock className="w-3 h-3" /> {fmt(h.created_at)} · {h.requested_by_name}
                      {h.redact ? " · redacted" : " · names shown"}{h.has_password ? " · password" : ""}
                    </div>
                  </div>
                  {h.status === "ready" ? (
                    <Button size="sm" variant="outline" onClick={() => doDownload(h)} data-testid={`history-download-${h.id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                  ) : h.status === "expired" ? (
                    <span className="text-xs text-amber-700 whitespace-nowrap">Link expired</span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
